import { IColumn, IDetailsGroupRenderProps, IGroup, IObjectWithKey } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPLink,
  IPPrimaryButton,
  IPSearchBox,
  IPSeparator,
  IPShimmeredDetailsList,
  IPText,
} from 'baseComponents';
import { IPCheckbox } from 'baseComponents/Checkbox/IPCheckbox';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import SupportTicketModal from 'components/Dialogs/SupportTicketModal';
import { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalFilterDelay } from 'globalConstants';
import { freshdeskWidgetOpen, freshdeskWidgetSetContactInfo, freshdeskWidgetSetTranslations } from 'globalFunctions';
import {
  backIcon,
  globalLayerRightSideBarProps,
  globalStackStylesPanel,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import Dashboard, { DashboardTypes } from 'models/dashboard';
import Widget, {
  getWidgetCategories,
  getWidgetTypes,
  IWidgetCategory,
  IWidgetType,
  Widget_Translation,
  WidgetCategories,
  WidgetSizes,
  WidgetTypes,
} from 'models/widget';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Config from 'services/Config/configService';
import { sortOnString } from 'utils/sorting';
import WidgetConfigRenderer from './WidgetConfigRenderer';
import WidgetSizeSelector from './WidgetSizeSelector';

interface IAddWidgetPanelProps {
  dashboard: Dashboard;
  isOpen: boolean;
  allowSharedDataWidgets: boolean;
  period?: IPeriodFilterData;
  onAdd: (widget: Widget) => void;
  onClose: () => void;
}

const AddWidgetPanel = (props: IAddWidgetPanelProps) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [categories, setCategories] = useState<IWidgetCategory[]>([]);
  const [showTicketModal, setShowTicketModal] = useState<boolean>(false);
  const [widgetTypes, setWidgetTypes] = useState<IWidgetType[]>([]);
  const [widgetsDisplay, setWidgetsDisplay] = useState<IWidgetType[]>([]);
  const [selectedWidget, setSelectedWidget] = useState<IWidgetType | undefined>(undefined);
  const [configuredWidget, setConfiguredWidget] = useState<Widget | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [groups, setGroups] = useState<IGroup[] | undefined>(undefined);
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [configError, setConfigError] = useState<boolean>(false);
  const [overWriteName, setOverWriteName] = useState<boolean>(false);
  const [widgetName, setWidgetName] = useState<string | undefined>(undefined);

  useEffect(() => {
    //clean-up
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createSelection = () => {
    return new IPSelection({
      getKey: (item: IWidgetType) => {
        return item.id;
      },
      onSelectionChanged: () => {
        const item = selection.getSelection()[0] as IWidgetType;
        setSelectedWidget(item);
      },
    });
  };

  const [selection] = React.useState(createSelection());

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      if (isLoading) return;

      setIsLoading(true);

      let categories = getWidgetCategories(appContext, t);
      categories.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(categories);

      let widgetTypes = getWidgetTypes(appContext, t);
      if (props.dashboard.dashboardType === DashboardTypes.partner) {
        categories = categories.filter((category) => category.id === WidgetCategories.Partner);
        widgetTypes = widgetTypes.filter((widget) => widget.category === WidgetCategories.Partner);
      } else if (props.dashboard.dashboardType === DashboardTypes.tenant) {
        //per-customer dashboard: only single-tenant widgets, no user-level or cross-tenant widgets
        widgetTypes = widgetTypes.filter((widget) => widget.tenantLevel === true);
        const tenantCategoryIds = new Set(widgetTypes.map((widget) => widget.category));
        categories = categories.filter((category) => tenantCategoryIds.has(category.id));
      } else if (props.dashboard.dashboardType === DashboardTypes.project) {
        widgetTypes = widgetTypes.filter(
          (widget) =>
            widget.type === WidgetTypes.OpenTasksPerUser ||
            widget.type === WidgetTypes.TaskStatus ||
            widget.type === WidgetTypes.TaskCompletedDeadline ||
            widget.type === WidgetTypes.MyOverdueTasks,
        );
        const projectCategoryIds = new Set(widgetTypes.map((widget) => widget.category));
        categories = categories.filter((category) => projectCategoryIds.has(category.id));
      } else {
        //all other dashboard types: hide dashboard-type-restricted categories
        //- Customer (partner) widgets are only available on the partner dashboard
        //- General widgets (environment overview) are only available on the per-customer (tenant) dashboard
        widgetTypes = widgetTypes.filter(
          (widget) => widget.category !== WidgetCategories.Partner && widget.category !== WidgetCategories.General,
        );
        categories = categories.filter(
          (category) => category.id !== WidgetCategories.Partner && category.id !== WidgetCategories.General,
        );
      }

      if (!props.allowSharedDataWidgets) {
        widgetTypes = widgetTypes.filter((w) => w.needSharedData === false);
      }

      widgetTypes.sort((a, b) => {
        if (a.category === b.category) {
          return sortOnString(a.name, b.name);
        } else {
          const catA = categories.find((c) => c.id === a.category);
          const catB = categories.find((c) => c.id === b.category);
          if (!catA || !catB) return 0;

          return sortOnString(catA.name, catB.name);
        }
      });

      setWidgetTypes(widgetTypes);
      setWidgetsDisplay(widgetTypes);

      calcGroups(categories, widgetTypes, searchText);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getGroups = (
    categories: IWidgetCategory[],
    widgets: IWidgetType[],
    searchText: string | undefined,
  ): IGroup[] | undefined => {
    let start: number = 0;

    const groups = categories.map((category) => {
      const count = widgets.filter((l) => l.category === category.id).length;
      const group: IGroup = {
        key: category.name,
        name: category.name,
        startIndex: start,
        count: count,
        level: 0,
        isCollapsed: searchText && count > 0 ? false : true,
      };
      start += count;

      return group;
    });

    return groups.filter((g) => g.count > 0);
  };

  const onUpdateConfig = (config: string, isValid: boolean) => {
    if (!configuredWidget) return;

    if (isValid) {
      const newWidget = configuredWidget.clone();
      newWidget.widgetConfig = config;
      setConfiguredWidget(newWidget);
    }

    setConfigError(!isValid);
  };

  const onSelect = () => {
    if (!selectedWidget) return;
    const newWidget = getDefConfigWidgetFromType(selectedWidget);
    setConfiguredWidget(newWidget);
  };

  const getDefConfigWidgetFromType = (widgetType: IWidgetType): Widget => {
    const newWidget = new Widget();
    newWidget.dashboard = props.dashboard;
    newWidget.dashboardId = props.dashboard.dashboardId;
    newWidget.name = widgetType.name;
    newWidget.widgetType = widgetType.type;
    newWidget.widgetSize = widgetType.defaultSize;

    return newWidget;
  };

  const onAddWidget = () => {
    if (!configuredWidget) return;
    props.onAdd(configuredWidget);
  };

  const calcGroups = (categories: IWidgetCategory[], widgets: IWidgetType[], searchText: string | undefined) => {
    setGroups(getGroups(categories, widgets, searchText));
  };

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (filterText) {
      const filteredWidgets = widgetTypes.filter(
        (widget) =>
          widget.name.toLowerCase().indexOf(filterText) >= 0 ||
          widget.description.toLowerCase().indexOf(filterText) >= 0,
      );
      setSearchText(filterText.toLowerCase());
      setWidgetsDisplay(filteredWidgets);
      calcGroups(categories, filteredWidgets, filterText);
    } else {
      setWidgetsDisplay(widgetTypes);
      calcGroups(categories, widgetTypes, undefined);
      setSearchText('');
    }
  };

  const onGroupClick = (group: IGroup) => {
    const listId = Number.parseInt(group.key);
    const category = categories.find((l) => l.id === listId);
    if (category) {
      const keysToSelect = widgetsDisplay.filter((l) => l.category === category.id).map((l) => l.id);
      keysToSelect.forEach((k) => selection.setKeySelected(k.toString(), true, false));
    }
  };

  const getGroupProps = (): IDetailsGroupRenderProps | undefined => {
    return {
      showEmptyGroups: false,
      isAllGroupsCollapsed: true,
      headerProps: {
        onGroupHeaderClick: onGroupClick,
      },
    };
  };

  const onColumnClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
    if (!column) return;
    sortByColumn(column.key, false);
  };

  const sortByColumn = (columnKey: string, keepDirection: boolean, setDescending?: boolean) => {
    const newColumns: IColumn[] = columns.slice();
    const currColumn = newColumns.find((currCol) => columnKey === currCol.key);
    if (!currColumn) return;

    newColumns.forEach((newCol: IColumn) => {
      if (newCol.key === currColumn.key) {
        if (!keepDirection) {
          if (setDescending !== undefined) {
            currColumn.isSortedDescending = setDescending;
          } else {
            currColumn.isSortedDescending = !currColumn.isSortedDescending;
          }
        }
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });

    const widgets = copyAndSort(widgetsDisplay, columnKey, currColumn.isSortedDescending);
    setColumns(newColumns);
    setWidgetTypes(widgets);

    calcGroups(categories, widgets, searchText);
  };

  const copyAndSort = (widgets: IWidgetType[], columnKey: string, isSortedDescending?: boolean): IWidgetType[] => {
    if (columnKey === 'name') {
      widgets.sort((a, b) => {
        if (a.category === b.category) {
          return a.name.localeCompare(b.name);
        } else {
          const catA = categories.find((c) => c.id === a.category);
          const catB = categories.find((c) => c.id === b.category);
          if (!catA || !catB) return 0;

          return catA.name.localeCompare(catB.name);
        }
      });
    }

    return widgets.slice();
  };

  const onSetWidgetName = (value: string | undefined) => {
    if (!configuredWidget) return;
    const widgetClone = configuredWidget.clone();
    widgetClone.name = value?.trimStart() ?? '';
    setWidgetName(widgetClone.name);

    // If the name is empty, clear the translations.
    if (value) {
      const translation = new Widget_Translation();
      translation.lang = appContext.user.language;
      translation.languageId = appContext.user.language.languageId;
      translation.name = widgetClone.name;
      widgetClone.trans = [translation];
      widgetClone.transIdx = 0;
    } else {
      widgetClone.trans = [];
    }

    setConfiguredWidget(widgetClone);
  };

  const onSetSize = (size: WidgetSizes) => {
    if (!configuredWidget) return;
    const widgetClone = configuredWidget.clone();
    widgetClone.widgetSize = size;
    setConfiguredWidget(widgetClone);
  };

  // When the checkbox is toggled, set the widget name to the cached value or clear it.
  const onSetOverwriteName = (value: boolean) => {
    onSetWidgetName(value ? widgetName : undefined);
    setOverWriteName(value);
  };

  const hasError = (): boolean => {
    return configError;
  };

  const openTicket = () => {
    if (Config.isHubSpotSupportEnabled()) {
      setShowTicketModal(true);
    } else {
      freshdeskWidgetOpen();
      freshdeskWidgetSetContactInfo(appContext.user.name, appContext.user.email);
      freshdeskWidgetSetTranslations();
    }
  };

  //
  // Render
  //

  const getColumns = (): IColumn[] => {
    return [
      {
        isMultiline: true,
        key: 'name',
        name: t('dashboard:AddWidgetPanel.Columns.Name'),
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        onRender: (item?: IWidgetType, index?: number, column?: IColumn) => {
          if (!item) return;

          return (
            <IPStack>
              <IPText>{item.name}</IPText>
            </IPStack>
          );
        },
      },
      {
        isMultiline: true,
        key: 'description',
        name: t('dashboard:AddWidgetPanel.Columns.Description'),
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        isCollapsible: true,
        onRender: (item?: IWidgetType, index?: number, column?: IColumn) => {
          if (!item) return;

          return <IPText>{item.description}</IPText>;
        },
      },
    ];
  };

  const [columns, setColumns] = React.useState<IColumn[]>(getColumns());

  const onRenderFooterContent = () => {
    return (
      <IPStack horizontal styles={globalStackStylesPanel} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPrimaryButton
            className="isoplanner-dashboard-widget-add-select"
            onClick={() => onSelect()}
            disabled={selectedWidget === undefined}
          >
            {t('translation:General.Button.Select')}
          </IPPrimaryButton>
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton onClick={props.onClose} disabled={isLoading}>
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack.Item>
      </IPStack>
    );
  };

  const onRenderFooterContentConfiguredWidget = () => {
    return (
      <IPStack horizontal styles={globalStackStylesPanel} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPrimaryButton
            className="isoplanner-dashboard-widget-add-save"
            onClick={() => onAddWidget()}
            disabled={selectedWidget === undefined || hasError()}
          >
            {t('translation:General.Button.Add')}
          </IPPrimaryButton>
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton onClick={props.onClose} disabled={isLoading}>
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack.Item>
      </IPStack>
    );
  };

  //
  // Main render
  //
  return (
    <>
      <IPPanel
        headerText={t('dashboard:AddWidgetPanel.Title')}
        type={IPPanelType.medium}
        isBlocking={true}
        isHiddenOnDismiss={false}
        isOpen={props.isOpen}
        onDismiss={props.onClose}
        isFooterAtBottom={true}
        layerProps={globalLayerRightSideBarProps}
        onRenderFooterContent={configuredWidget ? onRenderFooterContentConfiguredWidget : onRenderFooterContent}
      >
        {configuredWidget && (
          <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPStack horizontal verticalAlign="start">
                <IPStack.Item>
                  <IPIconButton
                    iconProps={backIcon}
                    onClick={() => {
                      setConfiguredWidget(undefined);
                    }}
                  />
                </IPStack.Item>
                <IPStack.Item>
                  <IPStack>
                    <IPText variant="large" styles={globalTextStylesBold}>
                      {selectedWidget?.name}
                    </IPText>
                    {!appContext.isMobileView && <IPText> {selectedWidget?.description}</IPText>}
                  </IPStack>
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
                <IPStack.Item grow>
                  <IPTextField
                    label={t('dashboard:AddWidgetPanel.NameLabel')}
                    autoFocus={!appContext.isMobileView}
                    disabled={!overWriteName}
                    placeholder={configuredWidget.getName(appContext, t)}
                    onChange={(ev, newValue) => onSetWidgetName(newValue)}
                    maxLength={128}
                  />
                </IPStack.Item>
                {configuredWidget.getType(widgetTypes)?.canAdjustSize && (
                  <IPStack.Item>
                    <WidgetSizeSelector widget={configuredWidget} onChange={onSetSize} />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <IPCheckbox
                checked={overWriteName}
                label={t('dashboard:AddWidgetPanel.OverwriteNameLabel')}
                onChange={(ev, checked) => {
                  onSetOverwriteName(checked ?? false);
                }}
              />
              <IPSeparator />
            </IPStack.Item>
            <IPStack.Item grow>
              <WidgetConfigRenderer
                widget={configuredWidget}
                onUpdateConfig={onUpdateConfig}
                editmode={false}
                period={props.period}
              />
            </IPStack.Item>
          </IPStack>
        )}
        {!configuredWidget && (
          <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapMedium}>
            <IPStack.Item>
              <IPText block>{t('dashboard:AddWidgetPanel.Info')}</IPText>
              <IPLink onClick={() => openTicket()}>{t('dashboard:AddWidgetPanel.MoreLink')}</IPLink>
            </IPStack.Item>
            <IPStack.Item>
              <IPSearchBox
                placeholder={t('translation:General.Filter.Placeholder')}
                underlined
                id="search"
                onChange={(ev, newValue) => {
                  if (searchTimer) {
                    clearTimeout(searchTimer);
                  }
                  setSearchTimer(setTimeout(() => onChangeText(ev, newValue), globalFilterDelay));
                }}
                styles={{ root: { width: 300 } }}
              />
            </IPStack.Item>
            <ScrollableStackItem isOnPanel={true}>
              <IPShimmeredDetailsList
                compact
                shimmerLines={5}
                enableShimmer={isLoading}
                layoutMode={IPDetailsListLayoutMode.justified}
                items={widgetsDisplay}
                columns={columns}
                groups={groups}
                groupProps={getGroupProps()}
                onColumnHeaderClick={onColumnClick}
                selectionMode={IPSelectionMode.single}
                selection={selection as IPSelection<IObjectWithKey>}
              />
            </ScrollableStackItem>
          </IPStack>
        )}
      </IPPanel>
      <SupportTicketModal
        isOpen={showTicketModal}
        onDismiss={() => setShowTicketModal(false)}
        initialSubject={t('dashboard:AddWidgetPanel.RequestWidgetTicketSubject')}
      />
    </>
  );
};

export default AddWidgetPanel;
